<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHeaderScroll } from '@/composables/useHeaderScroll'
import { useI18nRoute } from '@/composables/useI18nRoute'
import {
  NAVIGATION_LOCATIONS,
  useNavigationStore,
} from '@/stores/navigation.store'
import { useUIStore } from '@/stores/ui.store'
import OffCanvas from './OffCanvas.vue'
import Menu from './Menu.vue'
import UtilityControl from './UtilityControl.vue'
import UiIcon from '@/components/common/UiIcon.vue'

import { useI18n } from 'vue-i18n'
const { t } = useI18n()
const { localizedPath } = useI18nRoute()
const route = useRoute()
const { isSticky, isTop, isBottom } = useHeaderScroll(100)
const ui = useUIStore()
const navigationStore = useNavigationStore()
const { isOffcanvasOpen } = storeToRefs(ui)
const { closeOffcanvas, toggleOffcanvas } = ui
const mobileNavigation = computed(() =>
  navigationStore.findByLocation(NAVIGATION_LOCATIONS.mobile)
  ?? navigationStore.findByLocation(NAVIGATION_LOCATIONS.primary),
)
const hasMobileNavigationItems = computed(() => Boolean(mobileNavigation.value?.items.length))
const headerInnerRef = ref<HTMLElement | null>(null)
const isDevTestSwitcherOpen = ref(false)
const logoText = import.meta.env.VITE_APP_LOGO_TEXT?.trim() ?? ''
let devTestSwitcherCloseTimer: number | null = null

function clearDevTestSwitcherCloseTimer() {
  if (devTestSwitcherCloseTimer === null || typeof window === 'undefined') return

  window.clearTimeout(devTestSwitcherCloseTimer)
  devTestSwitcherCloseTimer = null
}

function closeDevTestSwitcher() {
  clearDevTestSwitcherCloseTimer()
  isDevTestSwitcherOpen.value = false
}

function scheduleDevTestSwitcherClose() {
  clearDevTestSwitcherCloseTimer()

  if (typeof window === 'undefined') {
    isDevTestSwitcherOpen.value = false
    return
  }

  devTestSwitcherCloseTimer = window.setTimeout(() => {
    isDevTestSwitcherOpen.value = false
    devTestSwitcherCloseTimer = null
  }, 140)
}

function handleHeaderFocusOut(event: FocusEvent) {
  const nextTarget = event.relatedTarget

  if (nextTarget instanceof Node && headerInnerRef.value?.contains(nextTarget)) {
    return
  }

  closeDevTestSwitcher()
}

function handlePointerDown(event: PointerEvent) {
  if (!isDevTestSwitcherOpen.value) return

  const target = event.target
  if (!(target instanceof Node)) return
  if (headerInnerRef.value?.contains(target)) return

  closeDevTestSwitcher()
}

function handleEscape(event: KeyboardEvent) {
  if (event.key !== 'Escape' || !isDevTestSwitcherOpen.value) return

  closeDevTestSwitcher()
}

function handleToggleOffcanvas() {
  if (!hasMobileNavigationItems.value) return

  closeDevTestSwitcher()
  toggleOffcanvas()
}

watch(() => route.fullPath, () => closeDevTestSwitcher())
watch(hasMobileNavigationItems, (hasItems) => {
  if (!hasItems) closeOffcanvas()
}, { immediate: true })

onMounted(() => {
  document.addEventListener('pointerdown', handlePointerDown)
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  clearDevTestSwitcherCloseTimer()
  document.removeEventListener('pointerdown', handlePointerDown)
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <header
    itemscope
    itemtype="https://schema.org/WPHeader"
    class="w-full absolute top-0 z-40"
  >
    <div
      class="headroom"
      :class="[
        isSticky ? 'headroom--unpinned' : 'headroom--pinned',
        {
          'headroom--top': isTop,
          'headroom--bottom': isBottom,
          'headroom--dropdown-active': isDevTestSwitcherOpen,
        }
      ]"
    >
      <div
        ref="headerInnerRef"
        class="headroom--inner relative overflow-visible"
        @pointerenter="clearDevTestSwitcherCloseTimer()"
        @pointerleave="scheduleDevTestSwitcherClose()"
        @focusin="clearDevTestSwitcherCloseTimer()"
        @focusout="handleHeaderFocusOut"
      >
        <div class="container max-w-screen-2xl mx-auto flex gap-2 lg:gap-4 justify-between items-center">
          <RouterLink
            class="my-4 inline-flex flex-row items-center gap-2 self-start text-base-content"
            :to="localizedPath('home')"
            aria-label="Gå til forsiden"
          >
            <UiIcon
              name="Brain"
              class="size-8 text-primary"
            />

            <h1
              id="logo"
              class="hidden md:block mb-0 text-2xl uppercase text-base-content font-bold"
            >
              {{ logoText }}
            </h1>
            <span class="sr-only">Lenke til forsiden</span>
          </RouterLink>

          <div class="hidden flex-1 justify-end lg:flex">
            <Menu
              role="navigation"
              aria-label="Primary"
            />
          </div>

          <UtilityControl
            :test-switcher-open="isDevTestSwitcherOpen"
            @interact="closeOffcanvas"
            @update:test-switcher-open="isDevTestSwitcherOpen = $event"
          />

          <nav
            v-if="hasMobileNavigationItems"
            class="flex items-center justify-end lg:hidden"
            aria-label="Mobile menu controls"
          >
            <button
              type="button"
              class="hamburger rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              :class="{ 'hamburger--is-open': isOffcanvasOpen }"
              :aria-expanded="isOffcanvasOpen"
              aria-controls="primary-menu"
              :aria-label="isOffcanvasOpen ? t('menu.closeMenu') : t('menu.openMenu')"
              @click="handleToggleOffcanvas"
            >
              <span class="hamburger__item hamburger__item--first"></span>
              <span class="hamburger__item hamburger__item--middle"></span>
              <span class="hamburger__item hamburger__item--last"></span>
            </button>
          </nav>
        </div>
      </div>
    </div>

    <OffCanvas
      v-if="hasMobileNavigationItems"
      role="navigation"
      :aria-label="t('menu.mobileMenu')"
      id="primary-menu"
    />
  </header>
</template>
